import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader } from "@/components/ui/Primitives";
import { KeyRound, RefreshCw, Link2, XCircle, DoorOpen, Timer, Users, Copy, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { usePageTitle } from "@/lib/seo";

export default function ButterflyMxIntegration() {
  usePageTitle("Butterfly MX integration — Central Rentals");
  const [status, setStatus] = useState(null);
  const [units, setUnits] = useState([]);
  const [entries, setEntries] = useState([]);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [form, setForm] = useState({ api_key: "", building_id: "", building_name: "" });
  const [pinForm, setPinForm] = useState({ label: "", valid_hours: 4, unit_number: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, e, p] = await Promise.all([
        api.get("/butterflymx/status").then((r) => r.data),
        api.get("/butterflymx/units").then((r) => r.data).catch(() => ({ items: [] })),
        api.get("/butterflymx/entry-log").then((r) => r.data).catch(() => ({ items: [] })),
        api.get("/butterflymx/pins").then((r) => r.data).catch(() => ({ items: [] })),
      ]);
      setStatus(s);
      setUnits(u.items || []);
      setEntries(e.items || []);
      setPins(p.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setB = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

  const connect = async (e) => {
    e.preventDefault();
    if (!form.api_key || !form.building_id) { toast.error("API key + building ID required"); return; }
    setB("connect", true);
    try {
      const { data } = await api.post("/butterflymx/connect", form);
      toast.success(data.sandbox ? "Sandbox connected." : `Connected to ${data.building_name}`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not connect"); }
    finally { setB("connect", false); }
  };
  const syncUnits = async () => {
    setB("units", true);
    try { const { data } = await api.post("/butterflymx/sync/units"); toast.success(`Synced ${data.count} unit(s).`); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Sync failed"); }
    finally { setB("units", false); }
  };
  const syncEntries = async () => {
    setB("entries", true);
    try { const { data } = await api.post("/butterflymx/sync/entry-log"); toast.success(`Synced ${data.count} entry event(s).`); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Sync failed"); }
    finally { setB("entries", false); }
  };
  const issuePin = async (e) => {
    e.preventDefault();
    if (!pinForm.label) { toast.error("Label required (e.g. 'HVAC vendor · Feb 14')"); return; }
    setB("pin", true);
    try {
      const { data } = await api.post("/butterflymx/pin/issue", pinForm);
      toast.success(`PIN ${data.pin_code} issued.`);
      setPinForm({ label: "", valid_hours: 4, unit_number: "" });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not issue PIN"); }
    finally { setB("pin", false); }
  };
  const revokePin = async (pin_id) => {
    try { await api.post(`/butterflymx/pins/${pin_id}/revoke`); toast.success("Revoked."); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Revoke failed"); }
  };
  const disconnect = async () => {
    if (!confirm("Disconnect Butterfly MX? Access + PINs stay on the intercom; only Central Rentals stops syncing.")) return;
    setB("disc", true);
    try { await api.post("/butterflymx/disconnect"); toast.success("Disconnected."); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Disconnect failed"); }
    finally { setB("disc", false); }
  };
  const copy = (v) => { try { navigator.clipboard.writeText(v); toast.success("Copied"); } catch { /* noop */ } };

  if (loading) return <Loader/>;
  const connected = !!status?.connected;

  return (
    <div className="space-y-6" data-testid="butterflymx-page">
      <PageHeader title="Butterfly MX" subtitle="Smart intercom & vendor delivery access." />

      {!connected ? (
        <Card className="p-6" data-testid="bmx-connect-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center flex-none"><Link2 className="w-5 h-5"/></div>
            <div>
              <h2 className="font-display text-lg font-bold text-slate-900">Connect your Butterfly MX building</h2>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Paste your Butterfly MX API key + building ID. To try the flow first, use <code className="px-1.5 py-0.5 rounded bg-slate-100 text-[12px]">sandbox_demo</code> — we&apos;ll populate 6 sample units, 10 entry events, and let you issue mock PINs.
              </p>
            </div>
          </div>
          <form onSubmit={connect} className="space-y-3 max-w-lg">
            <input type="password" placeholder="sandbox_demo or bmx_live_..." value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
              data-testid="bmx-api-key"/>
            <input type="text" placeholder="Building ID (bldg_...)" value={form.building_id}
              onChange={(e) => setForm({ ...form, building_id: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
              data-testid="bmx-building-id"/>
            <input type="text" placeholder="Label (optional) — e.g. Bay Street Towers" value={form.building_name}
              onChange={(e) => setForm({ ...form, building_name: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
              data-testid="bmx-building-name"/>
            <Button type="submit" disabled={busy.connect} data-testid="bmx-connect-btn">
              <Link2 className="w-4 h-4"/> {busy.connect ? "Connecting…" : "Connect Butterfly MX"}
            </Button>
          </form>
        </Card>
      ) : (
        <Card className="p-6 bg-gradient-to-br from-indigo-50 to-white border-indigo-200" data-testid="bmx-connected-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center flex-none"><DoorOpen className="w-5 h-5"/></div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-lg font-bold text-slate-900">Connected</h2>
                  {status.sandbox && <Pill tone="amber">Sandbox</Pill>}
                </div>
                <p className="text-sm text-slate-600 mt-1">Building: <b>{status.building_name || status.building_id}</b></p>
                {status.last_sync && <p className="text-xs text-slate-500 mt-0.5">Last sync: {new Date(status.last_sync).toLocaleString("en-CA")}</p>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={syncUnits} disabled={busy.units} data-testid="bmx-sync-units">
                <RefreshCw className={`w-4 h-4 ${busy.units ? "animate-spin" : ""}`}/> Sync units
              </Button>
              <Button onClick={syncEntries} disabled={busy.entries} data-testid="bmx-sync-entries">
                <RefreshCw className={`w-4 h-4 ${busy.entries ? "animate-spin" : ""}`}/> Sync entry log
              </Button>
              <Button variant="secondary" onClick={disconnect} disabled={busy.disc} data-testid="bmx-disconnect">
                <XCircle className="w-4 h-4"/> Disconnect
              </Button>
            </div>
          </div>
        </Card>
      )}

      {connected && (
        <div className="grid md:grid-cols-3 gap-3">
          <Card className="p-4"><div className="flex items-center gap-3"><Users className="w-4 h-4 text-slate-500"/><div><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Units</div><div className="font-display text-2xl font-extrabold text-slate-900" data-testid="bmx-summary-units">{units.length}</div></div></div></Card>
          <Card className="p-4"><div className="flex items-center gap-3"><DoorOpen className="w-4 h-4 text-slate-500"/><div><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Entries (last 24h)</div><div className="font-display text-2xl font-extrabold text-slate-900" data-testid="bmx-summary-entries">{entries.length}</div></div></div></Card>
          <Card className="p-4"><div className="flex items-center gap-3"><KeyRound className="w-4 h-4 text-slate-500"/><div><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Active PINs</div><div className="font-display text-2xl font-extrabold text-slate-900" data-testid="bmx-summary-pins">{pins.filter((p) => p.status === "active").length}</div></div></div></Card>
        </div>
      )}

      {connected && (
        <Card className="p-5" data-testid="bmx-pin-issue-card">
          <div className="flex items-center gap-2 mb-3"><KeyRound className="w-4 h-4 text-slate-600"/><h3 className="font-display font-bold text-slate-900">Issue a one-time vendor PIN</h3></div>
          <form onSubmit={issuePin} className="grid md:grid-cols-4 gap-2">
            <input value={pinForm.label} onChange={(e) => setPinForm({ ...pinForm, label: e.target.value })}
              placeholder="Label (HVAC vendor · Feb 14)"
              className="md:col-span-2 px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-emerald-500"
              data-testid="bmx-pin-label"/>
            <input type="number" min="1" max="168" value={pinForm.valid_hours}
              onChange={(e) => setPinForm({ ...pinForm, valid_hours: Number(e.target.value) || 4 })}
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-emerald-500"
              data-testid="bmx-pin-hours"/>
            <Button type="submit" disabled={busy.pin} data-testid="bmx-pin-submit"><KeyRound className="w-4 h-4"/> {busy.pin ? "Issuing…" : "Issue PIN"}</Button>
          </form>
        </Card>
      )}

      {connected && pins.length > 0 && (
        <Card className="p-5">
          <h3 className="font-display font-bold text-slate-900 mb-3">Recent PINs</h3>
          <div className="divide-y divide-slate-100" data-testid="bmx-pins-list">
            {pins.slice(0, 20).map((p) => (
              <div key={p.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`bmx-pin-${p.id}`}>
                <span className="font-mono text-lg font-bold text-slate-900 tracking-wider">{p.pin_code}</span>
                <button onClick={() => copy(p.pin_code)} className="text-slate-400 hover:text-slate-700"><Copy className="w-3.5 h-3.5"/></button>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{p.label}</div>
                  <div className="text-xs text-slate-500"><Timer className="w-3 h-3 inline mr-1"/>Expires {new Date(p.expires_at).toLocaleString("en-CA")}</div>
                </div>
                <Pill tone={p.status === "active" ? "green" : p.status === "revoked" ? "rose" : "slate"}>{p.status}</Pill>
                {p.status === "active" && (
                  <button onClick={() => revokePin(p.id)} className="text-xs font-semibold text-rose-700 hover:text-rose-900" data-testid={`bmx-revoke-${p.id}`}>
                    <ShieldOff className="w-3.5 h-3.5 inline mr-1"/>Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {connected && entries.length > 0 && (
        <Card className="p-5">
          <h3 className="font-display font-bold text-slate-900 mb-3">Recent entries</h3>
          <div className="divide-y divide-slate-100" data-testid="bmx-entries-list">
            {entries.slice(0, 15).map((e) => (
              <div key={e.bmx_id} className="py-2.5 flex items-center gap-3 text-sm">
                <Pill tone="slate">{e.unit_number}</Pill>
                <div className="flex-1 min-w-0 truncate"><b>{e.opened_by}</b> · {e.method.replace(/-/g, " ")}</div>
                <div className="text-xs text-slate-500 flex-none">{new Date(e.occurred_at).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
